/**
 * Результат опроса по площадке (ecology-survey, макет клиента 16.09): что нужно (с пояснением), что
 * требует актуализации, что уже есть (с датой) и пометки эколога. Строки уже добавлены в «Проектные
 * работы» и «Отчётность» — результат только показывается. Если категория площадки не указана —
 * пояснение, что категорию проставит эколог Центрики (после этого сервер сам пересчитает опрос).
 */
import type { ReactNode } from 'react'
import type { EcoCatalogItem, EcoSurveyResult, EcoSurveyResultItem } from '@/lib/api'
import { ECO_KIND_SHORT_LABELS, formatEcoDate } from '@/lib/ecology'
import { Card } from '@/design-system/components/layout/card'
import { Button } from '@/design-system/components/primitives/button'
import { StatusDot, type DotVariant } from '@/design-system/components/primitives/status-dot'
import { Alert } from '@/design-system/components/feedback/alert'

function Row({
  dot,
  title,
  meta,
  onOrder,
}: {
  dot: DotVariant
  title?: string
  meta: ReactNode
  /** Заказать эту позицию в Центрике (ecology-order). */
  onOrder?: () => void
}) {
  return (
    <li className="flex items-start gap-2.5 border-b border-line py-2.5 last:border-b-0">
      <StatusDot variant={dot} label="" aria-hidden className="mt-1.5" />
      <div className="min-w-0 flex-1">
        {title ? <div className="font-medium text-txt">{title}</div> : null}
        <div className="text-13 text-txt-secondary">{meta}</div>
      </div>
      {onOrder ? (
        <Button size="sm" variant="secondary" className="shrink-0" onClick={onOrder}>
          Заказать
        </Button>
      ) : null}
    </li>
  )
}

function itemMeta(item: EcoSurveyResultItem, fallback: string): string {
  const kind = ECO_KIND_SHORT_LABELS[item.kind]
  return [kind, item.explanations.length > 0 ? item.explanations.join('; ') : fallback]
    .filter(Boolean)
    .join(' · ')
}

export function SurveyResult({
  result,
  siteHasCategory,
  catalog,
  onOrder,
}: {
  result: EcoSurveyResult
  /** У площадки указана категория (иначе категорию по ответам проставит эколог). */
  siteHasCategory: boolean
  /** Справочник: кнопка «Заказать» только у позиций, которые Центрика берёт в заказ. */
  catalog: EcoCatalogItem[]
  /** Заказать недостающее в Центрике (ecology-order); у команды не показывается. */
  onOrder?: (catalogItemId: string) => void
}) {
  const needCount = result.need.length + result.update.length
  // Позиция может быть нужна клиенту, но не заказываться в Центрике («Экологический сбор» клиент
  // вносит и сдаёт сам) — по такой строке заказ тупиковый, кнопку не показываем.
  const orderable = (id: string) =>
    Boolean(onOrder) && catalog.some((c) => c.id === id && c.showInOrder)
  return (
    <div className="flex flex-col gap-4">
      {!siteHasCategory ? (
        <Alert variant="info" title="Категорию площадки проставит эколог Центрики">
          {result.categoryFromSurvey
            ? `По вашим ответам категория — ${result.category === 'none' ? 'без категории' : result.category}: документы подобраны по ней. Эколог проверит ответы и проставит категорию площадке.`
            : 'Эколог проверит ответы и проставит категорию площадки — тогда кабинет сам добавит документы, обязательные для этой категории.'}
        </Alert>
      ) : null}

      <div className="grid grid-cols-1 gap-4 nw:grid-cols-2">
        <Card header={`Нужно сделать · ${needCount}`}>
          {needCount === 0 && result.notes.length === 0 ? (
            <p className="text-13 text-txt-secondary">
              По вашим ответам недостающих документов нет.
            </p>
          ) : (
            <ul>
              {result.need.map((item) => (
                <Row
                  key={`need-${item.catalogItemId}`}
                  dot="red"
                  title={item.name}
                  meta={itemMeta(item, 'нужен по ответам опроса')}
                  onOrder={
                    onOrder && orderable(item.catalogItemId)
                      ? () => onOrder(item.catalogItemId)
                      : undefined
                  }
                />
              ))}
              {result.update.map((item) => (
                <Row
                  key={`update-${item.catalogItemId}`}
                  dot="amber"
                  title={item.name}
                  meta={itemMeta(item, 'требует актуализации')}
                  onOrder={
                    onOrder && orderable(item.catalogItemId)
                      ? () => onOrder(item.catalogItemId)
                      : undefined
                  }
                />
              ))}
              {result.notes.map((note) => (
                <Row key={`note-${note}`} dot="amber" meta={note} />
              ))}
            </ul>
          )}
        </Card>
        <Card header={`Уже есть · ${result.have.length}`}>
          {result.have.length === 0 ? (
            <p className="text-13 text-txt-secondary">Имеющихся документов не отмечено.</p>
          ) : (
            <ul>
              {result.have.map((item) => (
                <Row
                  key={`have-${item.catalogItemId}`}
                  dot="green"
                  title={item.name}
                  meta={[
                    ECO_KIND_SHORT_LABELS[item.kind],
                    item.date
                      ? `дата ${formatEcoDate(item.date)}`
                      : item.needsDate
                        ? 'дата не указана — дополните в строке'
                        : '',
                  ]
                    .filter(Boolean)
                    .join(' · ')}
                />
              ))}
            </ul>
          )}
        </Card>
      </div>
    </div>
  )
}
